import { useEffect } from "react";

import type { MeResponse } from "./api/types";
import { useAuth } from "./auth/AuthProvider";
import { hasPermission, landingPath } from "./auth/permissions";
import { AdminPage } from "./pages/admin/AdminPage";
import { AllBlocksPage } from "./pages/blocks/AllBlocksPage";
import { ApprovalsPage } from "./pages/blocks/ApprovalsPage";
import { BlockDetailPage } from "./pages/blocks/BlockDetailPage";
import { BlockImportPage } from "./pages/blocks/BlockImportPage";
import { BlocksDashboard } from "./pages/blocks/BlocksDashboard";
import { BlocksSeasonsPage } from "./pages/blocks/BlocksSeasonsPage";
import { EarningCurvePage } from "./pages/blocks/EarningCurvePage";
import { MyBlocksPage } from "./pages/blocks/MyBlocksPage";
import { PeoplePage } from "./pages/blocks/PeoplePage";
import { PillarsPage } from "./pages/blocks/PillarsPage";
import { SizerPage } from "./pages/blocks/SizerPage";
import { ForbiddenPage } from "./pages/ForbiddenPage";
import { GumballsPage } from "./pages/gumballs/GumballsPage";
import { HiringPage } from "./pages/hiring/HiringPage";
import { SignedOutPage } from "./pages/SignedOutPage";
import { VisionPage } from "./pages/vision/VisionPage";
import { Router, pathStartsWith, useRouter } from "./router/Router";
import { AppShell } from "./shell/AppShell";
import { ThemeProvider } from "./themes/ThemeProvider";

export function App() {
  const auth = useAuth();

  if (auth.status === "loading") {
    return (
      <ThemeProvider userId={null}>
        <SignedOutPage title="Loading Blocks" description="Checking your session…" />
      </ThemeProvider>
    );
  }

  if (auth.status === "unauthenticated") {
    return (
      <ThemeProvider userId={null}>
        <SignedOutPage
          title="Sign in to Blocks"
          description="Use your Queen One account to continue."
          action={
            <button type="button" className="btn btn--primary" onClick={auth.login}>
              Sign in with WorkOS
            </button>
          }
        />
      </ThemeProvider>
    );
  }

  if (auth.status === "forbidden") {
    return (
      <ThemeProvider userId={null}>
        <SignedOutPage
          title="Not authorized"
          description="Your account is signed in, but it does not have access to Blocks."
          action={
            <button type="button" className="btn btn--primary" onClick={auth.login}>
              Try another account
            </button>
          }
        />
      </ThemeProvider>
    );
  }

  if (auth.status === "error" || auth.me === null) {
    return (
      <ThemeProvider userId={null}>
        <SignedOutPage
          title="Could not load Blocks"
          description={auth.error ?? "Refresh the page or try signing in again."}
          action={
            <button type="button" className="btn btn--primary" onClick={() => void auth.refresh()}>
              Retry
            </button>
          }
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider userId={auth.me.user.id}>
      <Router>
        <AuthenticatedRoutes me={auth.me} onLogout={auth.logout} />
      </Router>
    </ThemeProvider>
  );
}

function AuthenticatedRoutes({
  me,
  onLogout
}: {
  me: MeResponse;
  onLogout: () => Promise<void>;
}) {
  const router = useRouter();

  // When the user lands on `/`, render the role-correct page on the FIRST
  // pass. Computing the effective path inline avoids the one-frame "Page not
  // found" flash that appears when matching is deferred to a useEffect.
  const path = router.path;
  const isRoot = path === "" || path === "/";
  const effectivePath = isRoot ? landingPath(me) : path;

  // Keep the URL bar in sync with the page we actually rendered. Replace
  // (no history entry) so Back doesn't bounce the user back to "/".
  useEffect(() => {
    if (isRoot && effectivePath !== path) {
      router.navigate(effectivePath, { replace: true });
    }
  }, [router, isRoot, effectivePath, path]);

  // The Blocks feature flag gates Block Ops + Vision. When off, those paths
  // fall through to the soft-404 below; Gumballs and admin are unaffected.
  const blocksEnabled = me.features.blocks;
  const isBlocks = blocksEnabled && pathStartsWith(effectivePath, "/blocks");
  const isVision = blocksEnabled && pathStartsWith(effectivePath, "/vision");
  const isGumballs = pathStartsWith(effectivePath, "/gumballs");
  const isAdmin = pathStartsWith(effectivePath, "/admin");
  // The Hiring feature flag gates the Open Positions surface. When off, /hiring
  // paths fall through to the soft-404 below.
  const isHiring = me.features.hiring && pathStartsWith(effectivePath, "/hiring");

  let content: React.ReactNode;
  let pageTitle: string;

  if (isBlocks) {
    if (!hasPermission(me, "blocks:view")) {
      content = <ForbiddenPage me={me} />;
      pageTitle = "Not authorized";
    } else if (pathStartsWith(effectivePath, "/blocks/mine")) {
      content = <MyBlocksPage me={me} />;
      pageTitle = "My Blocks";
    } else if (pathStartsWith(effectivePath, "/blocks/all")) {
      content = <AllBlocksPage me={me} />;
      pageTitle = "All Blocks";
    } else if (pathStartsWith(effectivePath, "/blocks/people")) {
      if (!hasPermission(me, "blocks:people:view")) {
        content = <ForbiddenPage me={me} />;
        pageTitle = "Not authorized";
      } else {
        content = <PeoplePage me={me} />;
        pageTitle = "Contributors";
      }
    } else if (pathStartsWith(effectivePath, "/blocks/approvals")) {
      if (!hasPermission(me, "blocks:approve_completion")) {
        content = <ForbiddenPage me={me} />;
        pageTitle = "Not authorized";
      } else {
        content = <ApprovalsPage />;
        pageTitle = "Approvals";
      }
    } else if (pathStartsWith(effectivePath, "/blocks/sizer")) {
      content = <SizerPage />;
      pageTitle = "Block Sizer";
    } else if (pathStartsWith(effectivePath, "/blocks/earning")) {
      if (!hasPermission(me, "blocks:earning:configure")) {
        content = <ForbiddenPage me={me} />;
        pageTitle = "Not authorized";
      } else {
        content = <EarningCurvePage me={me} />;
        pageTitle = "Earning Curve";
      }
    } else if (pathStartsWith(effectivePath, "/blocks/pillars")) {
      if (!hasPermission(me, "blocks:pillars:manage")) {
        content = <ForbiddenPage me={me} />;
        pageTitle = "Not authorized";
      } else {
        content = <PillarsPage />;
        pageTitle = "Pillars";
      }
    } else if (pathStartsWith(effectivePath, "/blocks/seasons")) {
      if (!hasPermission(me, "blocks:seasons:manage")) {
        content = <ForbiddenPage me={me} />;
        pageTitle = "Not authorized";
      } else {
        content = <BlocksSeasonsPage />;
        pageTitle = "Seasons";
      }
    } else if (pathStartsWith(effectivePath, "/blocks/import")) {
      if (!hasPermission(me, "blocks:create_top") || !hasPermission(me, "blocks:seasons:manage")) {
        content = <ForbiddenPage me={me} />;
        pageTitle = "Not authorized";
      } else {
        content = <BlockImportPage me={me} />;
        pageTitle = "Mass Block Upload";
      }
    } else {
      const detailId = blockDetailId(effectivePath);
      if (detailId !== null) {
        // key forces a remount per block so form state / fetch don't leak
        // across navigation between detail pages.
        content = <BlockDetailPage key={detailId} me={me} blockId={detailId} />;
        pageTitle = "Block Detail";
      } else {
        content = <BlocksDashboard me={me} />;
        pageTitle = "Dashboard";
      }
    }
  } else if (isVision) {
    if (!hasPermission(me, "blocks:view")) {
      content = <ForbiddenPage me={me} />;
      pageTitle = "Not authorized";
    } else {
      content = <VisionPage me={me} />;
      pageTitle = "Vision";
    }
  } else if (isGumballs) {
    if (!hasPermission(me, "gumballs:view")) {
      content = <ForbiddenPage me={me} />;
      pageTitle = "Not authorized";
    } else {
      content = <GumballsPage me={me} />;
      pageTitle = "Gumballs";
    }
  } else if (isAdmin) {
    content = <AdminPage me={me} path={effectivePath} />;
    pageTitle = adminPageTitle(effectivePath, me);
  } else if (isHiring) {
    if (!hasPermission(me, "positions:view")) {
      content = <ForbiddenPage me={me} />;
      pageTitle = "Not authorized";
    } else {
      content = <HiringPage me={me} path={effectivePath} />;
      pageTitle = effectivePath === "/hiring/departments" ? "Departments" : "Open Positions";
    }
  } else {
    // Unknown route — render a soft 404 inside the shell instead of bouncing,
    // and offer the two real entry points so the user is never stuck.
    content = (
      <div className="card empty-state">
        <h2>Page not found</h2>
        <p>The link you followed isn't part of Blocks yet.</p>
        <p>
          Try the <a href="/gumballs">gumball factory</a> or the{" "}
          <a href="/admin">admin panel</a>.
        </p>
      </div>
    );
    pageTitle = "Not found";
  }

  return (
    <AppShell me={me} pageTitle={pageTitle} path={effectivePath} onLogout={onLogout}>
      {content}
    </AppShell>
  );
}

// blockDetailId returns the id segment of a /blocks/<id> path, or null for the
// dashboard (/blocks) and the named sub-pages (/blocks/mine, /blocks/all,
// /blocks/people, /blocks/pillars, /blocks/seasons, /blocks/import,
// /blocks/approvals — handled before this is called; listed here as
// belt-and-braces against match-order changes).
function blockDetailId(path: string): string | null {
  const prefix = "/blocks/";
  if (!path.startsWith(prefix)) {
    return null;
  }
  const rest = path.slice(prefix.length).replace(/\/$/, "");
  if (
    rest === "" ||
    rest === "mine" ||
    rest === "all" ||
    rest === "people" ||
    rest === "pillars" ||
    rest === "seasons" ||
    rest === "import" ||
    rest === "approvals"
  ) {
    return null;
  }
  return rest;
}

function adminPageTitle(path: string, me: MeResponse): string {
  // When the route would render ForbiddenPage, swap the title to "Not
  // authorized" so the header reflects the error rather than the page the
  // user couldn't reach.
  const routes: { path: string; title: string; permission: string }[] = [
    { path: "/admin/ledger", title: "Ledger", permission: "gumballs:ledger:view" },
    { path: "/admin/coin-uploads", title: "Coin uploads", permission: "gumballs:coins:upload" },
    { path: "/admin/adjustments", title: "Adjustments", permission: "gumballs:balances:adjust" },
    { path: "/admin/seasons", title: "Gumball Seasons", permission: "gumballs:season:manage" },
    { path: "/admin/themes", title: "Themes", permission: "settings:manage" }
  ];
  for (const route of routes) {
    if (path !== route.path) {
      continue;
    }
    if (!hasPermission(me, route.permission)) {
      return "Not authorized";
    }
    return route.title;
  }
  if (!hasPermission(me, "members:view")) {
    return "Not authorized";
  }
  return "People";
}
